import styled from "@emotion/styled";
import { Box, Row, Space } from "@stenajs-webui/core";
import { stenaSearch } from "@stenajs-webui/elements";
import { Drawer } from "@stenajs-webui/modal";
import {
  NavBar,
  NavBarButton,
  NavBarHeading,
  SidebarMenu,
  SidebarRailMenu,
} from "@stenajs-webui/panels";
import cx from "classnames";
import type { FC } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation } from "react-router-dom";

import { BoxWithPrintHide } from "../../../common-ui/BoxWithPrintHide";
import { getModalRoot } from "../../../common-ui/modal/BaseModal";
import { showSearchOverlay } from "../../../services/search/searchActions";
import { useAccount } from "../../../services/settings/hooks/useAccount";
import {
  closeSidebar,
  disableSidebarRail,
  enableSidebarRail,
  toggleSidebar,
} from "../../../services/sidebar/sidebarActions";
import {
  getRailIsEnabled,
  getSidebarIsOpen,
} from "../../../services/sidebar/sidebarReducer";
import { useAuth } from "../../../use-cases/auth/AuthContext";
import { BookingSearch } from "../../../use-cases/manage/search/components/BookingSearch";
import { NotificationCenterIcon } from "../../../use-cases/notifications/components/NotificationCenterIcon";
import { FrontendOutOfDateBanner } from "../../header-banners/FrontendOutOfDateBanner";
import { MaintenanceMessageBanner } from "../../header-banners/MaintenanceMessageBanner";
import { useAuthState } from "../../hooks/useAuthState";
import { useIsDesktop } from "../../hooks/useIsDesktop";
import {
  getMainPage,
  isOnManagePage,
  isOnPaymentPage,
  isOnSimpleCreatePage,
} from "../../routes/routeUtils";
import { trackEvent } from "../../tracking/trackerService";
import { zIndices } from "../../zIndices";
import { AccountMenuButton } from "./AccountMenuButton";
import { SidebarBottomItems, SidebarTopItems } from "./SidebarItems";

const StyledNavBar = styled(NavBar)`
  & > div:nth-of-type(2) {
    flex: 0 1 350px;
  }
`;

const HeaderWrapper = styled("div")`
  .manage-page & {
    position: sticky;
    left: 0;
    width: 100dvw;
  }

  @media print {
    -webkit-print-color-adjust: exact !important;
    display: block;
    width: 100%;
    height: 60px;
  }
`;

export const useRailEnabled = () => useSelector(getRailIsEnabled);

export const Header: FC = () => {
  const auth = useAuth();
  const { isAuthenticated } = useAuthState();
  const sidebarIsOpen = useSelector(getSidebarIsOpen);
  const isDesktop = useIsDesktop();
  const account = useAccount();
  const drawerWidth = !isDesktop ? "100dvw" : "280px";

  const railEnabled = useRailEnabled() && isDesktop;

  const railMenuLabel = railEnabled ? "Unpin menu" : "Pin menu";

  const dispatch = useDispatch();

  const location = useLocation();

  const showSearch =
    isAuthenticated &&
    (isOnManagePage(location) ||
      isOnSimpleCreatePage(location) ||
      isOnPaymentPage(location));

  const onLogOut = () => {
    window.sessionStorage.clear();
    auth.logout();
  };

  // Shift the heading past the pinned rail. NavBar used to always reserve an
  // 86px slot on the left; stenajs-webui 23.14.0 stopped rendering it when
  // showMenuButton is false, which is exactly our rail-pinned case.
  // See https://github.com/stena-line-ab/stenajs-webui/pull/1360
  const headingMarginLeft = railEnabled
    ? "calc(var(--nav-menu-width) + var(--swui-metrics-indent) * 3)"
    : undefined;

  const stenaLineLink = (
    <Link
      to={getMainPage(account.permissions)}
      style={{ textDecoration: "none", marginLeft: headingMarginLeft }}
    >
      <NavBarHeading>Freight Portal</NavBarHeading>
    </Link>
  );

  const right = (
    <>
      <Space />
      <Row alignItems={"center"} gap={2}>
        {!isDesktop && showSearch && (
          <NavBarButton
            className={"t_search"}
            leftIcon={stenaSearch}
            onClick={() => dispatch(showSearchOverlay())}
          />
        )}
        <AccountMenuButton onLogOut={onLogOut} />
        <NotificationCenterIcon />
      </Row>
    </>
  );

  return (
    <>
      <HeaderWrapper>
        <StyledNavBar
          showMenuButton={!railEnabled}
          left={stenaLineLink}
          className={"t_main_navbar tour_navbar"}
          onClickMenuButton={() => dispatch(toggleSidebar())}
          center={showSearch && <BookingSearch isWide={isDesktop} />}
          right={right}
        />
        <Box style={{ marginLeft: "var(--nav-menu-width)" }}>
          <FrontendOutOfDateBanner />
          <MaintenanceMessageBanner />
        </Box>
      </HeaderWrapper>
      <Drawer
        isOpen={sidebarIsOpen}
        onRequestClose={() => dispatch(closeSidebar())}
        zIndex={zIndices.drawer}
        portalTarget={getModalRoot("routeless-modal-root")}
        width={drawerWidth}
      >
        <SidebarMenu
          onCloseClick={() => dispatch(closeSidebar())}
          pinButtonVisible={isDesktop}
          isPinned={railEnabled}
          onClickPinButton={() => {
            trackEvent({ category: "Navigation", action: railMenuLabel });
            if (railEnabled) {
              dispatch(disableSidebarRail());
            } else {
              dispatch(enableSidebarRail());
            }
            dispatch(closeSidebar());
          }}
          bottomItems={
            <SidebarBottomItems
              onRequestClose={() => dispatch(closeSidebar())}
              location={location}
            />
          }
        >
          <SidebarTopItems
            onRequestClose={() => dispatch(closeSidebar())}
            location={location}
          />
        </SidebarMenu>
      </Drawer>
      {railEnabled && (
        <BoxWithPrintHide
          zIndex={zIndices.drawer}
          className={cx("tour_rail", "t_pinned_sidebar")}
        >
          <SidebarRailMenu
            onClickMenuButton={() => dispatch(toggleSidebar())}
            closeButtonVisible
            onClickCloseButton={() => {
              trackEvent({ category: "Navigation", action: railMenuLabel });
              dispatch(disableSidebarRail());
            }}
            bottomItems={
              <SidebarBottomItems
                onRequestClose={() => dispatch(closeSidebar())}
                location={location}
                rail
              />
            }
          >
            <SidebarTopItems
              onRequestClose={() => dispatch(closeSidebar())}
              location={location}
              rail
            />
          </SidebarRailMenu>
        </BoxWithPrintHide>
      )}
    </>
  );
};
